import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../libs/shared/helpers/src/types/customErrors';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {useConstant} from '../../../srcCommon/helpers/hookHelpers';
import {AnswerFolderModel} from '../../models/api/answerFolderModel';
import {SettingsAnswersUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {AnswerFolderNameAlreadyExistsError} from '../../store/data/answerFolders/answerFolderActions';
import {AnswerFolderTemplate} from '../../store/data/answerFolders/saveFolderActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsFooter} from '../common/settingsWrapper';
import {getSettingsType} from '../rules/ruleHelpers';
import {settingsHorizontalPaddingStyles} from '../settingsCommonConstants';
import {SettingsAnswersHeaderContainer} from './settingsAnswersHeaderContainer';
import {SettingsSingleAnswerParentFolderField} from './settingsSingleAnswerParentFolderField';

/*
 * Props.
 */

interface SettingsSingleAnswerFolderProps {
  answersUrl: SettingsAnswersUrl;
  prefilledFolderId?: number;
  navigateToFolder: HandlerOf<number>;
  openRoot: Handler;
  folder?: AnswerFolderModel;
  isEditing?: boolean;
  onDeleteClick: Handler;
  onSaveClick: (template: AnswerFolderTemplate) => Promise<void | AnswerFolderModel>;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  overflow: auto;

  position: relative;
`;

const StyledHeaderButtons = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 8px;
`;

const StyledSettingsMain = styled.div`
  flex: 1;
  padding-bottom: 100px;
  ${settingsHorizontalPaddingStyles};
  display: flex;
  padding-right: 0;
  max-width: 650px;
`;

const StyledSettingsContent = styled.div`
  flex: 1;
  min-height: min-content;

  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  grid-row-gap: 16px;
  margin-top: 7px;
`;

const StyledSettingsField = styled.div`
  display: flex;
`;
const StyledTextBox = styled(TextBox)`
  width: 440px;
`;

/*
 * Component.
 */

export const SettingsSingleAnswerFolder: FC<SettingsSingleAnswerFolderProps> = (props) => {
  const {answersUrl, navigateToFolder, openRoot, folder, isEditing, onDeleteClick, prefilledFolderId} = props;

  const initialFolderState = useConstant(() => ({
    name: (isEditing && folder?.name) || '',
    parentFolderId: prefilledFolderId || folder?.parentFolderId,
    namespace: (isEditing && folder?.namespace) || answersUrl.namespace,
  }));

  const {namespace} = answersUrl;

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(!folder);
  const [error, setError] = useState<CustomError | undefined>(undefined);
  const [name, setName] = useState(initialFolderState.name);
  const [folderId, setFolderId] = useState(initialFolderState.parentFolderId);

  const {showBanner} = useSettingsBanners();

  const onCancelClick = () => {
    if (initialFolderState.parentFolderId) {
      props.navigateToFolder(initialFolderState.parentFolderId);
    } else {
      props.openRoot();
    }
  };

  const renderHeaderRight = () => (
    <StyledHeaderButtons>
      <Button
        size={VisualSizesEnum.MEDIUM}
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        color={PaletteColorsEnum.RED}
        onClick={onDeleteClick}
      >
        <Trans id="settings-deletefolder">Delete folder</Trans>
      </Button>
    </StyledHeaderButtons>
  );

  const canSave = useMemo(() => !isSaving && Boolean(name && namespace), [isSaving, name, namespace]);

  const onOkClick = async () => {
    try {
      setError(undefined);
      setIsSaving(true);
      setIsDirty(false);

      const template: AnswerFolderTemplate = {
        name,
        namespace,
        folderId,
      };

      const newFolder = await props.onSaveClick(template);

      if (isEditing) {
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="foldereditor-update-success">
              Folder &quot;{template.name}&quot; has been updated
            </Trans>
          ),
        });
        if (newFolder) {
          trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_FOLDER_CREATE, {
            canned_answer_folder_id: newFolder.id,
            parent_folder_id: folderId,
            if_nested_parent_folder: Boolean(folderId),
            settings_type: getSettingsType(answersUrl.namespace),
          });
        }
      } else {
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="foldereditor-create-success">
              Folder &quot;{template.name}&quot; has been created
            </Trans>
          ),
        });

        if (folder) {
          trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_FOLDER_UPDATE, {
            canned_answer_folder_id: folder.id,
            if_changed_name: template.name !== initialFolderState.name,
            if_changed_parent_folder: template.folderId !== folder.parentFolderId,
            settings_type: getSettingsType(answersUrl.namespace),
          });
        }
      }

      if (template.folderId) {
        props.navigateToFolder(template.folderId);
      } else {
        props.openRoot();
      }
    } catch (caughtError) {
      setIsSaving(false);
      setIsDirty(true);

      if (caughtError instanceof CustomError) {
        setError(caughtError);
      } else {
        throw caughtError;
      }
    }
  };

  const maybeRenderFooter = () => {
    if (!isDirty) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancelClick}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOkClick} isDisabled={!canSave}>
          {isEditing ? (
            <Trans id="settingstags-savetag">Save</Trans>
          ) : (
            <Trans id="settingstags-createtag">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  return (
    <StyledDiv>
      <SettingsAnswersHeaderContainer
        answersUrl={answersUrl}
        navigateToFolder={navigateToFolder}
        openRoot={openRoot}
        right={renderHeaderRight()}
        isEditing={isEditing}
      />
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledSettingsField>
            <StyledTextBox
              label={<Trans id="foldereditor-name">Name</Trans>}
              value={name}
              onChange={(newName) => {
                setIsDirty(true);
                setName(newName);
              }}
              isRequired
              message={
                <Trans id="foldereditor-namelabel">
                  Give your folder a name so you can quickly find it later.
                </Trans>
              }
              shouldAutoFocus
              errorMessage={
                error instanceof AnswerFolderNameAlreadyExistsError && (
                  <Trans id="answer-folder-create-name-exist-error">This name is already used</Trans>
                )
              }
              hasError={error !== undefined}
            />
          </StyledSettingsField>
          <SettingsSingleAnswerParentFolderField
            editedFolder={folder}
            allAvailableNamespaces={[namespace]}
            onFolderSelect={(selectedFolderId) => {
              setFolderId(selectedFolderId);
              setIsDirty(true);
            }}
            selectedFolderId={folderId}
            title={<Trans id="foldereditor-filetitle">File folder under</Trans>}
            subtitle={
              <Trans id="foldereditor-filesubtitle">Organize this folder under an existing folder</Trans>
            }
          />
        </StyledSettingsContent>
      </StyledSettingsMain>
      {maybeRenderFooter()}
    </StyledDiv>
  );
};
